import type { Blood } from '../graph';
import { cap, en, generationCaption, lower, ordinal, pick } from '../labels.en';
import type { Gender } from '../types';
import { addressFor, type AddressWords } from './address';
import { segments, type Segment } from './segments';
import type { Kin, KinPack, Sex, Via } from './types';

type G = Gender | Sex | undefined;

function greatPrefix(n: number): string {
  if (n <= 0) return '';
  if (n === 1) return en.great;
  return `${n}× ${en.great}`;
}

function ancestorWord(d: number, g: G): string {
  if (d === 1) return pick(g, en.parent);
  const base = pick(g, en.grand);
  if (d === 2) return cap(base);
  return cap(greatPrefix(d - 2) + base);
}

function descendantWord(d: number, g: G): string {
  if (d === 1) return pick(g, en.child);
  const base = pick(g, en.grandChild);
  if (d === 2) return cap(base);
  return cap(greatPrefix(d - 2) + base);
}

function stepify(word: string, d: number): string {
  return d === 1 ? `Step${lower(word)}` : `${en.step}${lower(word)}`;
}

/** English label for a blood shape and how the connection runs (PRD 7.2 table). */
export function labelFor(blood: Exclude<Blood, null>, via: Via, g: G): string {
  if ('up' in blood) {
    const w = ancestorWord(blood.up, g);
    if (via === 'mine') return w + en.inLaw;
    if (via === 'theirs') return stepify(w, blood.up);
    return w;
  }
  if ('down' in blood) {
    const w = descendantWord(blood.down, g);
    if (via === 'mine') return stepify(w, blood.down);
    if (via === 'theirs') return w + en.inLaw;
    return w;
  }
  const { a, b } = blood;
  const inLaw = via !== 'blood';
  if (a === 1 && b === 1) {
    const w = pick(g, en.sibling);
    return inLaw ? w + en.inLaw : w;
  }
  if (b === 1 && a >= 2) {
    // target is a parent's sibling
    return cap(greatPrefix(a - 2) + lower(pick(g, en.auntUncle)));
  }
  if (a === 1 && b >= 2) {
    // target is a sibling's descendant
    let w: string;
    if (b === 2) w = pick(g, en.nieceNephew);
    else w = cap(greatPrefix(b - 3) + lower(pick(g, en.grandniece)));
    return via === 'theirs' ? w + en.inLaw : w;
  }
  // a >= 2 && b >= 2
  const degree = Math.min(a, b) - 1;
  const diff = Math.abs(a - b);
  if (diff === 0) {
    const base = `${ordinal(degree)} ${en.cousin}`;
    return inLaw ? base.replace(/^(\S+ \S+)/, `$1${en.inLaw}`) : base;
  }
  // Plain English instead of "removed": say whose child (or whose cousin) they are.
  const cousin = degree === 1 ? en.cousin : `${ordinal(degree)} ${en.cousin}`;
  const base =
    b > a
      ? `${cap(cousin)}’s ${lower(descendantWord(diff, g))}`
      : `${ancestorWord(diff, null)}’s ${cousin}`;
  return inLaw ? `${base} ${en.byMarriage}` : base;
}

function noun(s: Segment): string {
  switch (s.rel) {
    case 'up':
      return lower(ancestorWord(s.depth, s.sex));
    case 'down':
      return lower(descendantWord(s.depth, s.sex));
    case 'sibling':
      return (s.half ? 'half-' : '') + lower(pick(s.sex, en.sibling));
    case 'spouse':
      return lower(pick(s.sex, en.spouse));
  }
}

export const enPack: KinPack = {
  id: 'en',
  name: 'English',
  lang: 'en',
  dir: 'ltr',
  status: 'ready',
  label(k: Kin): string {
    switch (k.kind) {
      case 'none':
        return '';
      case 'self':
        return en.you;
      case 'spouse':
        return pick(k.sex, en.spouse);
      case 'blood':
      case 'marriage':
        return labelFor(k.shape!, k.via, k.sex);
      case 'stepSibling':
        return stepify(pick(k.sex, en.sibling), 1);
      case 'coParentInLaw':
        return pick(k.sex, en.coParentInLaw);
      case 'relative':
        return en.relative;
    }
  },
  generation: generationCaption,
  explain(k: Kin): string {
    const segs = segments(k.path);
    if (segs.length < 2) return '';
    return `Your ${segs.map(noun).join('’s ')}`;
  },
  address: (k, name) => addressFor(EN_ADDRESS, k, name),
};

/** English forms of address (PRD 7.2.4): uncles and aunts by title and name; brothers, sisters and in-laws by name. */
const EN_ADDRESS: AddressWords = {
  parent: { m: ['Dad', 'Daddy'], f: ['Mom', 'Mum', 'Mommy'] },
  grandparent: (_side, sex, great) => (great ? (sex === 'f' ? ['Great-grandma'] : ['Great-grandpa']) : sex === 'f' ? ['Grandma', 'Granny'] : ['Grandpa', 'Granddad']),
  grandUncle: { m: ['Uncle {name}', 'Great-uncle {name}'], f: ['Aunt {name}', 'Great-aunt {name}'] },
  elder: { m: [], f: [] },
  uncle: {
    FB: { any: ['Uncle {name}'] },
    FZ: { any: ['Aunt {name}', 'Auntie {name}'] },
    MB: { any: ['Uncle {name}'] },
    MZ: { any: ['Aunt {name}', 'Auntie {name}'] },
  },
  uncleSpouse: {
    FB: { any: ['Aunt {name}', 'Auntie {name}'] },
    FZ: { any: ['Uncle {name}'] },
    MB: { any: ['Aunt {name}', 'Auntie {name}'] },
    MZ: { any: ['Uncle {name}'] },
  },
  parentInLaw: { m: [], f: [] },
  elderSiblingSpouse: { m: [], f: [] },
  coParentInLaw: { m: [], f: [] },
};
